<template>
  <b-modal
    id="add-or-update-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="md"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            Add Appointment
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onCreate)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
        
            <b-row>
              <b-col cols="12" md="12">
                <validation-provider #default="validationContext" name="Doctor" rules="">
                  <b-form-group label-for="description">
                    <template #label>
                      <span>Doctor<span style="color: tomato">*</span></span>
                    </template>
                    
                    <doctor-select 
                      :profession="'doctor'"
                      :value="form_data.doctor_id"
                      @change="val => form_data.doctor_id = val"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="12">
                <b-form-group label="Appointment Date & Time" label-for="appointment date & time">
                  <template #label>
                    <span>New Appointment Date & Time<span style="color: tomato">*</span></span>
                  </template>

                  <validation-provider
                    #default="{ errors }"
                    name="appointment date & time"
                    rules="required"
                  >
                    <b-input-group>
                      <DatetimePickerVue 
                        v-model="form_data.appointment_date" 
                        :color="'#077DBC'"
                        :button-color="'#65A30D'"
                        :format="'YYYY-MM-DD HH:mm'"
                        no-label
                      />
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
  
            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
              >
                Save & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>
      
<script>
import {
  BOverlay, BSidebar, BForm, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BInputGroup, BFormGroup, BRow, BCol, BFormDatepicker
} from 'bootstrap-vue'

import { required } from '@validations'
import { get, cloneDeep } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import DatetimePickerVue from 'datetime-picker-vue';
import 'datetime-picker-vue/dist/datetime-picker-vue.css';

import DoctorSelect from "@core/components/shared/admin/selects/DoctorSelect.vue"
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
  
const rawData = {
  appointment_date: '',
  patient_id: null,
  doctor_id: null,
}
      
export default {
  components: {
    BRow,
    BCol,
    BForm,
    BAlert,
    BModal,
    BButton,
    vSelect,
    BOverlay,
    BSidebar,
    BCardText,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormDatepicker,
    BFormInvalidFeedback,
    
    DatetimePickerVue,
    BInputGroup,
    BFormGroup,
    // Form Validation
    ValidationProvider,
    ValidationObserver,

    DoctorSelect
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    resource: {
      type: String,
      required: true
    },
    isAddActive: {
      type: Boolean,
      required: true,
    },
    patientData: {
      type: Object,
      required: true,
    },
    consultationData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      form_data: cloneDeep(rawData),
      currentDate: null,
    }
  },
  created(){
    this.currentDate = new Date()
    if (this.currentUserProfession === 'doctor' || this.currentUserProfession === 'dietician') {
      this.form_data.doctor_id = this.currentUser.id
    }
  },
  methods: {
    async onCreate(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.can(`${this.resource}:CanUpdate`)) {
          return
        }

        const { appointment_date, doctor_id: medical_professional_id } = this.form_data

        // Check if selected date is past
        const formatted_selected_date = new Date(appointment_date)

        if (formatted_selected_date.setHours(0, 0, 0, 0) < this.currentDate.setHours(0, 0, 0, 0)) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: 'You cannot select a past date'
            },
          });
          this.form_data.appointment_date = null;
          return
        }

        await this.useJwt().mainService.consultations.referToMedicalProfessional(this.consultationData.id, { 
          appointment_date, 
          medical_professional_id, 
          patient_id: this.patientData._id
        });

        this.$emit('update:is-add-active', false);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Appointment scheduled successfully.`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
        this.resetForm()
      }
    },
    resetForm() {
      this.form_data = cloneDeep(rawData);
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    },
  }
}
</script>
    
<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
</style>
